import { MacroRegion } from "enum/MacroRegion";
import { LatLngLiteral } from "leaflet";
import { DBAction } from "../../../enum/DBAction";
import { OperationRuleAlertNotificationType } from "../../../enum/OperationRuleAlertNotificationType";
import { ILocationMenuStation, ILocationMenuInsightCondition } from "../operationRuleLocations/OperationRuleLocationModel";

export type AlertChannel = "whatsapp" | "email" | "push_notification";

/** Sensor alert type discriminator (spec 043). NULL on persisted side normalized to 'sensor'. */
export type SensorAlertType = "sensor" | "lightning";

/**
 * Lightning slice of sensorAlertConfig — present iff the alert row is of type='lightning'
 * (autonomous alerts) or iff the event has a lightning row alongside an optional rain row.
 *
 * `anchor` is read-through metadata: legacy frontend preserves it across read-modify-write
 * when the user does NOT move the point, and clears it (sends `null`) on any movement.
 * The legacy never introduces UX to set or visualize the anchor.
 */
export interface ISensorAlertConfigLightning {
  point: { lat: number; lng: number };
  /** Monitoring radius in km, validated server-side ∈ [1, 100]. */
  radiusKm: number;
  /** Silence window in minutes ∈ {1, 5, 10, 30, 60}. */
  allClearMin: number;
  /** Notification window: 0 = Único (limit_change), {60, 120, 180, 360, 720, 1440} = Recorrente. */
  notificationWindowMin: number;
  /** Anchor to a saved forecast station — both fields together or neither. */
  anchor?: { stationId: number; regionId: number };
}

export interface IOperationRuleAlert {
  id: number;
  name: string;
  active: boolean;
  /**
   * Type discriminator. NULL on persisted side normalized to 'sensor'.
   * Added by spec 043 (FR-013).
   */
  type?: SensorAlertType;
  operationRuleLocationItemId?: number;
  notifications?: IOperationRuleAlertNotification[];
  recipients: IOperationRuleAlertUser[];
  alertTemplateId?: number;
  conditionGroups?: ConditionGroup[];
  conditions?: ILocationMenuInsightCondition[]; // For insight alerts, returned by backend
  geofence?: LatLngLiteral[][] | LatLngLiteral[];
  macroRegionOrigin?: MacroRegion;
  alertType?: string;
  area?: string;
  locationId?: number;
  channels?: AlertChannel[];
  stationId?: number | null;
  regionId?: number | null;
  station?: ILocationMenuStation;
  /** Sensor alert configuration — returned by GET (per-sensor conditions), sent on PUT (top-level conditions) */
  sensorAlertConfig?: {
    sensors: {
      id?: number;
      stationId: number;
      regionId: number;
      dataTypeId?: number;
      dataSourceId?: number;
      conditions?: ConditionGroup[];
    }[];
    conditions?: ConditionGroup[];
    alertFrequency?: number;
    /** Lightning slice — present iff the event has a lightning row (spec 043). */
    lightning?: ISensorAlertConfigLightning;
  } | null;
}
export interface IOperationRuleAlertNotification {
  id: number;
  title: string;
  description: string;
  type: OperationRuleAlertNotificationType;
  minutesType?: number; // Optional for backward compatibility
  minAdvanceTime?: number;
  maxAdvanceTime?: number;
  minimumEventDuration?: number;
  startTime?: string;
  endTime?: string;
  action?: DBAction;
  sendUpdates?: boolean;
  sendCancellations?: boolean;
}

export interface IOperationRuleAlertUser {
  id: number;
  userId: number;
  userName: string;
  action?: DBAction;
  isWhatsappAllowed?: boolean;
  isPushAllowed?: boolean;
  isEmailAllowed?: boolean;
}
export interface IOperationRuleAlertNotificationForTable {
  id: number;
  title: string;
  description: string;
}

export interface IOperationRuleAlertForTable {
  id: number;
  name: string;
  operationRuleLocationItemId?: number;
  operationRuleLocationItemName?: string;
  operationRuleLocationId?: number;
  operationRuleLocationName?: string;
  operationRuleId?: number;
  operationRuleName?: string;
  alertType?: string;
  createdBy?: number;
  active?: boolean;
  area?: string;
  notifications?: IOperationRuleAlertNotificationForTable[];
  /**
   * Sensor alert type discriminator (spec 043 FR-010). NULL on persisted side
   * normalized to 'sensor'. Drives the new "Tipo" column rendering.
   */
  type?: SensorAlertType;
}
export interface EnvironmentalRule {
  units: string;
  value: number;
  mathOperator: ">" | "<" | ">=" | "<=" | "==" | "!=";
  environmentalVariable: string;
}

export interface Conditions {
  rule: EnvironmentalRule[];
  LogicalOperator: "and" | "or";
}

/**
 * All supported alert/risk types (backendKey values)
 * Maps to RISK_TYPES in AlertTypeSelector.tsx
 */
export type AlertType =
  | "port_closure"
  | "extreme_wind"
  | "extreme_rain"
  | "rough_sea"
  | "tree_fall"
  | "roof_damage"
  | "landslide"
  | "extreme_heat"
  | "wildfire"
  | "fog"
  | "low_visibility"
  | "lightning_storm"
  | "others";

export interface AlertConfig {
  id: number;
  /** Macro region origin (e.g., "eps", "gfs"). Only present in custom templates, not in default templates. */
  macroregiao?: string;
  configId: string;
  active: boolean;
  geofence: number[][];
  conditions: ConditionGroup[];
  alertType: AlertType;
  area: string;
}

/**
 * Response structure from the alert-template-config endpoint
 * Contains separate arrays for custom (user-defined) and default (system) templates
 */
export interface AlertTemplateConfigResponse {
  custom: AlertConfig[];
  default: AlertConfig[];
}

export enum AlertModule {
  INSIGHT = "INSIGHT",
  NEW = "NEW",
}
export interface OperationRuleAlertForTableAPIParams {
  operationRuleId?: number;
  operationRuleLocationId?: number;
  alertType?: string;
  operationRuleLocationItemIds?: number[];
}

export interface ConditionGroup {
  id: string;
  parentId: string | null;
  groupOperator: string; // ex: "and", "or"
  conditionOperator: string; // ex: "and", "or"
  conditions: Condition[];
}

export interface Condition {
  environmentalVariable: string;
  environmentalVariableId: number;
  comparisonOperator: string;
  value: number;
}
